/**
 * Admin Panel
 * Displays user management, activity tracking, and error logs
 * Only visible to admin user (chandra.kuncara@gmail.com)
 */

import React, { useState, useEffect } from 'react';
import { getAllUsers, getVersionUpdates, getVersionStats, deleteVersionUpdate, getTelegramStats } from '../services/adminApi';
import { getAllFeedbackAdmin, replyToFeedback, resolveFeedback } from '../services/feedbackApi';
import ErrorLogsDashboard from '../components/ErrorLogsDashboard';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('email');
  const [sortOrder, setSortOrder] = useState('asc');
  // Feedback state
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  // Version updates state
  const [versionUpdates, setVersionUpdates] = useState([]);
  const [versionStats, setVersionStatsData] = useState(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionError, setVersionError] = useState(null);

  // Telegram stats state
  const [telegramStats, setTelegramStats] = useState(null);
  const [telegramStatsLoading, setTelegramStatsLoading] = useState(false);
  const [telegramStatsError, setTelegramStatsError] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'feedback') {
      fetchFeedbacks();
    }
    if (activeTab === 'version-updates') {
      fetchVersionUpdates();
    }
    if (activeTab === 'telegram-stats') {
      fetchTelegramStats();
    }
  }, [activeTab, feedbackStatusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllUsers();
      setUsers(response.data.users);
    } catch (err) {
      setError(err.message);
      console.error('❌ Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Feedback functions
  const fetchFeedbacks = async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const filters = feedbackStatusFilter === 'all' ? {} : { status: feedbackStatusFilter };
      const response = await getAllFeedbackAdmin({ ...filters, limit: 100 });
      setFeedbacks(response.data?.feedbacks || []);
    } catch (err) {
      setFeedbackError(err.message);
      console.error('Failed to fetch feedbacks:', err);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleReplySubmit = async (feedbackId) => {
    if (!replyText.trim()) return;
    setReplyingTo(feedbackId);
    try {
      await replyToFeedback(feedbackId, replyText.trim());
      setReplyText('');
      setExpandedFeedback(null);
      fetchFeedbacks();
    } catch (err) {
      console.error('Failed to send reply:', err);
      alert('Failed to send reply: ' + err.message);
    } finally {
      setReplyingTo(null);
    }
  };

  const handleResolve = async (feedbackId) => {
    if (!window.confirm('Mark this feedback as resolved?')) return;
    try {
      await resolveFeedback(feedbackId);
      fetchFeedbacks();
    } catch (err) {
      console.error('Failed to resolve feedback:', err);
      alert('Failed to resolve: ' + err.message);
    }
  };

  // Version update functions
  const fetchVersionUpdates = async () => {
    setVersionLoading(true);
    setVersionError(null);
    try {
      const [updatesResponse, statsResponse] = await Promise.all([
        getVersionUpdates({ limit: 50 }),
        getVersionStats()
      ]);
      setVersionUpdates(updatesResponse.data?.updates || []);
      setVersionStatsData(statsResponse.data || null);
    } catch (err) {
      setVersionError(err.message);
      console.error('Failed to fetch version updates:', err);
    } finally {
      setVersionLoading(false);
    }
  };

  const handleDeleteVersionUpdate = async (updateId) => {
    if (!window.confirm('Delete this version update entry?')) return;
    try {
      await deleteVersionUpdate(updateId);
      fetchVersionUpdates();
    } catch (err) {
      console.error('Failed to delete version update:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  const fetchTelegramStats = async () => {
    setTelegramStatsLoading(true);
    setTelegramStatsError(null);
    try {
      const response = await getTelegramStats();
      setTelegramStats(response.data);
    } catch (err) {
      setTelegramStatsError(err.message);
      console.error('Failed to fetch Telegram stats:', err);
    } finally {
      setTelegramStatsLoading(false);
    }
  };

  const formatVersionDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFeedbackDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFeedbackStatusBadge = (status) => {
    const statusStyles = {
      submitted: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
      replied: { backgroundColor: '#dcfce7', color: '#16a34a' },
      resolved: { backgroundColor: '#E8E8E8', color: '#374151' }
    };
    const labels = {
      submitted: 'Submitted',
      replied: 'Replied',
      resolved: 'Resolved'
    };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '14px',
        fontSize: '12px',
        fontWeight: '500',
        ...statusStyles[status]
      }}>
        {labels[status] || status}
      </span>
    );
  };

  const sortUsers = (usersToSort) => {
    return [...usersToSort].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'created':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'expenses':
          aVal = a.activity.totalExpenses;
          bVal = b.activity.totalExpenses;
          break;
        case 'receipts':
          aVal = a.activity.receiptUploads;
          bVal = b.activity.receiptUploads;
          break;
        case 'ai_chats':
          aVal = a.activity.aiChats;
          bVal = b.activity.aiChats;
          break;
        default:
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const styles = {
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: 'var(--space-6)',
      fontFamily: 'var(--font-family-sans)'
    },
    header: {
      marginBottom: 'var(--space-8)'
    },
    title: {
      fontSize: 'var(--font-size-3xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--color-text-primary)',
      margin: '0 0 var(--space-2) 0'
    },
    subtitle: {
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-secondary)',
      margin: 0
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 'var(--space-4)',
      marginBottom: 'var(--space-8)'
    },
    statCard: {
      backgroundColor: 'var(--color-bg-base)',
      padding: 'var(--space-6)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)',
      textAlign: 'center',
      border: '1px solid var(--color-border-light)',
      transition: 'var(--transition-normal)'
    },
    statLabel: {
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-secondary)',
      marginBottom: 'var(--space-2)',
      fontWeight: 'var(--font-weight-medium)'
    },
    statValue: {
      fontSize: 'var(--font-size-4xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--color-primary)'
    },
    tableContainer: {
      backgroundColor: 'var(--color-bg-base)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)',
      overflow: 'hidden',
      border: '1px solid var(--color-border-light)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: 'var(--space-3) var(--space-4)',
      textAlign: 'left',
      backgroundColor: 'var(--color-bg-subtle)',
      borderBottom: '2px solid var(--color-border-light)',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-secondary)',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'var(--transition-normal)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    thHover: {
      backgroundColor: 'var(--color-bg-muted)'
    },
    td: {
      padding: 'var(--space-3) var(--space-4)',
      borderBottom: '1px solid var(--color-border-light)',
      fontSize: 'var(--font-size-sm)',
      color: 'var(--color-text-primary)'
    },
    tdCenter: {
      textAlign: 'center'
    },
    error: {
      backgroundColor: 'var(--color-error-light)',
      color: 'var(--color-error-dark)',
      padding: 'var(--space-4)',
      borderRadius: 'var(--radius-default)',
      marginBottom: 'var(--space-6)',
      fontSize: 'var(--font-size-sm)'
    },
    loading: {
      textAlign: 'center',
      padding: 'var(--space-10)',
      fontSize: 'var(--font-size-base)',
      color: 'var(--color-text-secondary)'
    },
    emptyState: {
      textAlign: 'center',
      padding: 'var(--space-10)',
      color: 'var(--color-text-secondary)'
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={fetchUsers}
          style={{
            padding: '8px 16px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Admin Panel</h1>
          <p style={styles.subtitle}>User management and activity tracking</p>
        </div>
        <div style={styles.emptyState}>
          <p>No users found</p>
        </div>
      </div>
    );
  }

  const sortedUsers = sortUsers(users);

  // Calculate totals
  const totalUsers = users.length;
  const totalExpenses = users.reduce((sum, u) => sum + u.activity.totalExpenses, 0);
  const totalReceipts = users.reduce((sum, u) => sum + u.activity.receiptUploads, 0);
  const totalAIChats = users.reduce((sum, u) => sum + u.activity.aiChats, 0);

  const getSortIndicator = (field) => {
    if (sortBy === field) {
      return sortOrder === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const tabStyles = {
    tabContainer: {
      display: 'flex',
      gap: 'var(--space-2)',
      marginBottom: 'var(--space-8)',
      padding: '4px',
      backgroundColor: '#F5F5F5',
      borderRadius: '12px',
      width: 'fit-content'
    },
    tab: {
      padding: 'var(--space-2) var(--space-4)',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      color: '#71717A',
      transition: 'var(--transition-normal)',
      appearance: 'none'
    },
    tabActive: {
      color: 'var(--color-text-primary)',
      backgroundColor: 'white',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Admin Dashboard</h1>
        <p style={styles.subtitle}>User management and activity overview</p>
      </div>

      {/* Tab Navigation */}
      <div style={tabStyles.tabContainer}>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeTab === 'users' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveTab('users')}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = activeTab === 'users' ? 'var(--color-text-primary)' : '#71717A';
          }}
        >
          👥 Users
        </button>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeTab === 'error-logs' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveTab('error-logs')}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = activeTab === 'error-logs' ? 'var(--color-text-primary)' : '#71717A';
          }}
        >
          📊 Error Logs
        </button>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeTab === 'feedback' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveTab('feedback')}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = activeTab === 'feedback' ? 'var(--color-text-primary)' : '#71717A';
          }}
        >
          💬 Feedback
        </button>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeTab === 'version-updates' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveTab('version-updates')}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = activeTab === 'version-updates' ? 'var(--color-text-primary)' : '#71717A';
          }}
        >
          🚀 Version Updates
        </button>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeTab === 'telegram-stats' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveTab('telegram-stats')}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = activeTab === 'telegram-stats' ? 'var(--color-text-primary)' : '#71717A';
          }}
        >
          ✈️ Telegram Stats
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
      {/* Stats Summary */}
      <div style={styles.statsGrid}>
        <div
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.06)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={styles.statLabel}>👥 Total Users</div>
          <div style={styles.statValue}>{totalUsers}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.06)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={styles.statLabel}>💰 Total Expenses</div>
          <div style={styles.statValue}>${totalExpenses}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.06)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={styles.statLabel}>📸 Receipt Uploads</div>
          <div style={styles.statValue}>{totalReceipts}</div>
        </div>
        <div
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.06)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={styles.statLabel}>🤖 AI Chats</div>
          <div style={styles.statValue}>{totalAIChats}</div>
        </div>
      </div>

      {/* Users Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th
                style={{...styles.th, cursor: 'pointer'}}
                onClick={() => toggleSort('email')}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Email {getSortIndicator('email')}
              </th>
              <th
                style={{...styles.th, cursor: 'pointer'}}
                onClick={() => toggleSort('created')}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Joined {getSortIndicator('created')}
              </th>
              <th
                style={{...styles.th, ...styles.tdCenter, cursor: 'pointer'}}
                onClick={() => toggleSort('expenses')}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Total {getSortIndicator('expenses')}
              </th>
              <th
                style={{...styles.th, ...styles.tdCenter, cursor: 'pointer'}}
                onClick={() => toggleSort('receipts')}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Receipts {getSortIndicator('receipts')}
              </th>
              <th
                style={{...styles.th, ...styles.tdCenter}}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Manual
              </th>
              <th
                style={{...styles.th, ...styles.tdCenter}}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Statements
              </th>
              <th
                style={{...styles.th, ...styles.tdCenter}}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-muted)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)'}
              >
                Telegram username
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
              <tr key={user.id} style={{backgroundColor: '#ffffff'}}>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td style={{...styles.td, ...styles.tdCenter}}>
                  <strong>{user.activity.totalExpenses}</strong>
                </td>
                <td style={{...styles.td, ...styles.tdCenter}}>
                  {user.activity.receiptUploads}
                </td>
                <td style={{...styles.td, ...styles.tdCenter}}>
                  {user.activity.manualEntries}
                </td>
                <td style={{...styles.td, ...styles.tdCenter}}>
                  {user.activity.statementUploads}
                </td>
                <td style={{...styles.td, ...styles.tdCenter}}>
                  {(() => {
                    // Prefer live Telegram stats (can contain display name) matched by email
                    const match = telegramStats?.rows?.find(r => r.email === user.email);
                    if (match) {
                      if (match.telegram_username) {
                        return `@${match.telegram_username}`;
                      }
                      if (match.telegram_display_name) {
                        return match.telegram_display_name;
                      }
                    }
                    // Fallback to stored username attached to user
                    return user.telegram_username ? `@${user.telegram_username}` : '—';
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}

      {/* Error Logs Tab */}
      {activeTab === 'error-logs' && (
        <ErrorLogsDashboard />
      )}

      {/* Feedback Tab */}
      {activeTab === 'feedback' && (
        <section style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', letterSpacing: '-0.025em' }}>User Feedback</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>Filter:</label>
              <select
                value={feedbackStatusFilter}
                onChange={(e) => setFeedbackStatusFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-light)',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All</option>
                <option value="submitted">Submitted</option>
                <option value="replied">Replied</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>

          {feedbackLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
              Loading feedback...
            </div>
          )}

          {feedbackError && (
            <div style={{ ...styles.error, marginBottom: '16px' }}>
              <strong>Error:</strong> {feedbackError}
            </div>
          )}

          {!feedbackLoading && feedbacks.length === 0 && !feedbackError && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <div>No feedback submissions found.</div>
            </div>
          )}

          {feedbacks.map((feedback) => (
            <div
              key={feedback.id}
              style={{
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '12px',
                border: '1px solid var(--color-border-light)',
                boxShadow: expandedFeedback === feedback.id ? '0 0 0 1px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.06)' : '0 0 0 1px rgba(0,0,0,0.03), 0 2px 4px rgba(0,0,0,0.04)'
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedFeedback(expandedFeedback === feedback.id ? null : feedback.id)}
              >
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                    {feedback.user_email || 'Unknown User'}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    {formatFeedbackDate(feedback.created_at)}
                    {feedback.feedback_images?.length > 0 && (
                      <span style={{ marginLeft: '8px' }}>📷 {feedback.feedback_images.length}</span>
                    )}
                  </div>
                </div>
                {getFeedbackStatusBadge(feedback.status)}
              </div>

              {/* Message Preview/Full */}
              <div style={{
                fontSize: '14px',
                color: 'var(--color-text-primary)',
                lineHeight: '1.5',
                marginBottom: expandedFeedback === feedback.id ? '16px' : 0
              }}>
                {expandedFeedback === feedback.id
                  ? feedback.message
                  : feedback.message.length > 150
                    ? feedback.message.substring(0, 150) + '...'
                    : feedback.message
                }
              </div>

              {/* Expanded Content */}
              {expandedFeedback === feedback.id && (
                <>
                  {/* Images */}
                  {feedback.feedback_images?.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                      {feedback.feedback_images.map((img, idx) => (
                        <img
                          key={img.id}
                          src={img.signedUrl}
                          alt={`Attachment ${idx + 1}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            border: '1px solid var(--color-border-light)'
                          }}
                          onClick={() => setModalImage(img.signedUrl)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Existing Reply */}
                  {feedback.admin_reply && (
                    <div style={{
                      backgroundColor: '#f0fdf4',
                      borderRadius: '10px',
                      padding: '12px',
                      marginBottom: '16px',
                      borderLeft: '3px solid #22c55e'
                    }}>
                      <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: '500', marginBottom: '4px' }}>
                        Your Reply:
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>
                        {feedback.admin_reply}
                      </div>
                      {feedback.admin_reply_at && (
                        <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
                          {formatFeedbackDate(feedback.admin_reply_at)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reply Form */}
                  {feedback.status !== 'resolved' && (
                    <div style={{ marginBottom: '12px' }}>
                      <textarea
                        placeholder="Write a reply..."
                        value={expandedFeedback === feedback.id ? replyText : ''}
                        onChange={(e) => setReplyText(e.target.value)}
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          padding: '10px',
                          borderRadius: '10px',
                          border: '1px solid var(--color-border-light)',
                          fontSize: '14px',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    {feedback.status !== 'resolved' && (
                      <>
                        <button
                          onClick={() => handleReplySubmit(feedback.id)}
                          disabled={!replyText.trim() || replyingTo === feedback.id}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: replyText.trim() ? '#3b82f6' : '#A1A1AA',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: replyText.trim() ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          {replyingTo === feedback.id ? 'Sending...' : 'Send Reply'}
                        </button>
                        <button
                          onClick={() => handleResolve(feedback.id)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Mark Resolved
                        </button>
                      </>
                    )}
                    {feedback.status === 'resolved' && (
                      <div style={{
                        padding: '8px 16px',
                        backgroundColor: '#F5F5F5',
                        borderRadius: '10px',
                        fontSize: '13px',
                        color: 'var(--color-text-secondary)'
                      }}>
                        Resolved on {formatFeedbackDate(feedback.resolved_at)}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Expand indicator */}
              <div
                style={{
                  textAlign: 'center',
                  marginTop: '8px',
                  fontSize: '12px',
                  color: 'var(--color-text-tertiary)',
                  cursor: 'pointer'
                }}
                onClick={() => setExpandedFeedback(expandedFeedback === feedback.id ? null : feedback.id)}
              >
                {expandedFeedback === feedback.id ? 'Click to collapse' : 'Click to expand'}
              </div>
            </div>
          ))}

          {/* Image Modal */}
          {modalImage && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px'
              }}
              onClick={() => setModalImage(null)}
            >
              <button
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
                onClick={() => setModalImage(null)}
              >
                ×
              </button>
              <img
                src={modalImage}
                alt="Full size"
                style={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  objectFit: 'contain',
                  borderRadius: '10px'
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </section>
      )}

      {/* Version Updates Tab */}
      {activeTab === 'version-updates' && (
        <section style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', letterSpacing: '-0.025em' }}>Version Updates</h2>
            <button
              onClick={fetchVersionUpdates}
              style={{
                padding: '8px 16px',
                backgroundColor: '#71717A',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Refresh
            </button>
          </div>

          {/* Stats */}
          {versionStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                backgroundColor: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-light)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: '600', color: '#3b82f6', letterSpacing: '-0.025em' }}>{versionStats.totalUpdates}</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Total Updates</div>
              </div>
              <div style={{
                backgroundColor: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-light)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: '600', color: '#10b981', letterSpacing: '-0.025em' }}>{versionStats.updatesLast30Days}</div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Last 30 Days</div>
              </div>
              <div style={{
                backgroundColor: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-light)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
                  {versionStats.latestVersion || 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Latest Version</div>
              </div>
              <div style={{
                backgroundColor: 'white',
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-light)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                  {versionStats.latestDeployedAt ? formatVersionDate(versionStats.latestDeployedAt) : 'N/A'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Last Deploy</div>
              </div>
            </div>
          )}

          {/* Loading/Error */}
          {versionLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
              Loading version updates...
            </div>
          )}

          {versionError && (
            <div style={{ ...styles.error, marginBottom: '16px' }}>
              <strong>Error:</strong> {versionError}
            </div>
          )}

          {/* Updates Table */}
          {!versionLoading && versionUpdates.length === 0 && !versionError && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🚀</div>
              <div>No version updates recorded yet. Updates are recorded automatically on each deployment.</div>
            </div>
          )}

          {!versionLoading && versionUpdates.length > 0 && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '10px',
              border: '1px solid var(--color-border-light)',
              overflow: 'hidden'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Version</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Summary</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Deployed</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Details</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {versionUpdates.map((update, index) => {
                      const cleanDetails = update.details
                        ? update.details.replace(/^\[[\da-f]+\]\n?/i, '').trim()
                        : '';

                      return (
                        <tr
                          key={update.id}
                          style={{
                            borderBottom: index < versionUpdates.length - 1 ? '1px solid #f1f5f9' : 'none',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAFAFA'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td style={{ padding: '12px 16px', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              backgroundColor: '#dbeafe',
                              color: '#1d4ed8',
                              borderRadius: '10px',
                              fontWeight: '600',
                              fontSize: '12px'
                            }}>
                              {update.version || `#${update.update_number}`}
                            </span>
                            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px', textAlign: 'center' }}>
                              #{update.update_number}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', verticalAlign: 'top', fontWeight: '500', color: 'var(--color-text-primary)' }}>
                            {update.summary}
                          </td>
                          <td style={{ padding: '12px 16px', verticalAlign: 'top', whiteSpace: 'nowrap', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                            {formatVersionDate(update.deployed_at)}
                          </td>
                          <td style={{ padding: '12px 16px', verticalAlign: 'top', color: 'var(--color-text-secondary)', fontSize: '13px', lineHeight: '1.5', whiteSpace: 'pre-wrap', maxWidth: '400px' }}>
                            {cleanDetails || '-'}
                          </td>
                          <td style={{ padding: '12px 16px', verticalAlign: 'top', textAlign: 'center' }}>
                            <button
                              onClick={() => handleDeleteVersionUpdate(update.id)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: 'transparent',
                                color: '#ef4444',
                                border: '1px solid #fecaca',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Telegram Stats Tab */}
      {activeTab === 'telegram-stats' && (
        <section style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', letterSpacing: '-0.025em' }}>Telegram Bot Usage</h2>
            <button
              onClick={fetchTelegramStats}
              disabled={telegramStatsLoading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#71717A',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: telegramStatsLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: telegramStatsLoading ? 0.6 : 1
              }}
            >
              {telegramStatsLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {telegramStatsError && (
            <div style={{ ...styles.error, marginBottom: '16px' }}>
              <strong>Error:</strong> {telegramStatsError}
            </div>
          )}

          {telegramStatsLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
              Loading Telegram stats...
            </div>
          )}

          {!telegramStatsLoading && telegramStats && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-light)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '600', color: '#3b82f6', letterSpacing: '-0.025em' }}>{telegramStats.totalUsers}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Linked Users</div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-light)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '600', color: '#10b981', letterSpacing: '-0.025em' }}>{telegramStats.totalIncoming}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Incoming (user → bot)</div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '16px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-light)',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '600', color: '#8b5cf6', letterSpacing: '-0.025em' }}>{telegramStats.totalOutgoing}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>Outgoing (bot → user)</div>
                </div>
              </div>

              {(!telegramStats.rows || telegramStats.rows.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>✈️</div>
                  <div>No users have linked Telegram or chatted with the bot yet.</div>
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-light)',
                  overflow: 'hidden'
                }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Telegram ID</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Username</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>App Email</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Incoming</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Outgoing</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {telegramStats.rows.map((row, index) => (
                          <tr
                            key={row.telegram_user_id}
                            style={{
                              borderBottom: index < telegramStats.rows.length - 1 ? '1px solid #f1f5f9' : 'none'
                            }}
                          >
                            <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '13px' }}>{row.telegram_user_id}</td>
                            <td style={{ padding: '12px 16px' }}>
                              {row.telegram_username
                                ? `@${row.telegram_username}`
                                : (row.telegram_display_name || '—')}
                            </td>
                            <td style={{ padding: '12px 16px', color: 'var(--color-text-primary)' }}>{row.email || '—'}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '500' }}>{row.incoming_count}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '500' }}>{row.outgoing_count}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>{row.total_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}

export default AdminPanel;
